/* TL pre-order notes: all cart routes, existing carts, and a fresh checkout gate.
 * Refresh preorder properties and migrate the old membership selling plan.
 * Membership cart quantity is limited to one. Other quantities, inventory,
 * prices, existing contracts and orders do not change.
 * Internal changes use the captured fetch to avoid recursive reconciliation.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.install(root);
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';
  function membershipRepairFor(data) {
    var items = data.items.filter(function (item) { return item.membership || Number(item.id) === 39505661919326; });
    if (!items.length) return null;
    function invalid(message) { var error = new Error(message); error.customerMessage = message; throw error; }
    var destination = Number(data.membership_plan_id);
    if (!Number.isSafeInteger(destination) || destination <= 0) {
      invalid('Please remove the Essentials Club membership from your cart and add it again before checking out.');
    }
    for (var i = 0; i < items.length; i++) {
      var plan = Number(items[i].selling_plan_id);
      if (Number(items[i].id) !== 39505661919326 || (plan !== destination && plan !== 6088654942)) {
        invalid('Please remove the Essentials Club membership from your cart and add it again before checking out.');
      }
    }
    var item = items.find(function (line) { return Number(line.selling_plan_id) === destination; }) || items[0];
    if (items.length > 1) {
      var extra = items.find(function (line) { return line.key !== item.key; });
      return { id: extra.key, quantity: 0 };
    }
    var current = Number(item.selling_plan_id);
    if (current === destination && Number(item.quantity) === 1) return null;
    return { id: item.key, quantity: 1, selling_plan: destination };
  }

  function propertiesFor(item) {
    var current = item.properties || {};
    if (Array.isArray(current) || typeof current !== 'object') throw new Error('Invalid cart-line properties');
    var desired = Object.assign({}, current);
    // Keep gift, subscription, bundle and customer properties. Refresh only our
    // standardized note, preserving any unrelated text in the shared Note field.
    var otherNote = String(current.Note || '').split('\n').filter(function (line) {
      return !/^Pre-ordered item\. Shipment starts on /.test(line) &&
        !/^Pre-order\. (We will email you the shipment date\.|See the product page for the shipment date\.)$/.test(line);
    }).join('\n').trim();
    if (item.backorder) {
      desired.Note = (otherNote ? otherNote + '\n' : '') + item.note;
      desired._is_fabrikator_preorder = item.batch;
    } else {
      // A restock or disabled pre-order must also repair saved carts. Blank
      // values clear our metadata without removing/re-adding the cart line:
      // Shopify ignores an empty properties object. Preserve unrelated notes.
      if (current._is_fabrikator_preorder) desired._is_fabrikator_preorder = '';
      if (otherNote !== String(current.Note || '').trim()) desired.Note = otherNote;
    }
    return Object.keys(desired).some(function (key) { return desired[key] !== current[key]; }) ? desired : null;
  }
  function install(w) {
    if (w.__tlPreorderCartInstalled) return;
    w.__tlPreorderCartInstalled = true;
    var doc = w.document, rawFetch = w.fetch.bind(w);
    var base = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    var tail = Promise.resolve(), checkoutBusy = false, checkoutControl = null, timer;
    function queue(work) {
      var next = tail.catch(function () {}).then(work);
      tail = next.catch(function () {});
      return next;
    }
    async function request(url, init) {
      var response = await rawFetch(url, Object.assign({ credentials: 'same-origin', cache: 'no-store' }, init));
      if (!response.ok) throw new Error('Pre-order cart check failed (' + response.status + ')');
      return response;
    }
    async function state() {
      var response = await request(base + 'cart?section_id=tl-preorder-cart-state&_tlpo=' + Date.now());
      var dom = new w.DOMParser().parseFromString(await response.text(), 'text/html');
      var node = dom.querySelector('[data-tl-preorder-cart-state]');
      if (!node) throw new Error('Pre-order cart state is unavailable');
      var data = JSON.parse(node.textContent);
      if (data.version !== 1 || !Array.isArray(data.items)) throw new Error('Invalid pre-order cart state');
      return data;
    }
    async function reconcile() {
      var changed = false;
      // Re-read after each change: Shopify changes keys and can merge lines when
      // properties match. Never reuse a stale line number; preserve the fresh quantity.
      for (var attempt = 0; attempt < 64; attempt++) {
        var data = await state(), item = null, props = null;
        var membershipRepair = membershipRepairFor(data);
        if (membershipRepair) {
          await request(base + 'cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(membershipRepair)
          });
          changed = true;
          continue; // Changing a plan changes the line key: re-read before property repair.
        }
        for (var i = 0; i < data.items.length; i++) {
          props = propertiesFor(data.items[i]);
          if (props) { item = data.items[i]; break; }
        }
        if (!item) {
          doc.documentElement.dataset.tlPreorderCart = 'ready';
          doc.documentElement.dataset.tlPreorderBackorder = String(data.items.some(function (line) { return line.backorder; }));
          return changed;
        }
        // Shopify defaults omitted quantity to one. Send the fresh quantity.
        // Full merging
        // is essential: Shopify replaces, rather than merges, properties.
        await request(base + 'cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props })
        });
        changed = true;
      }
      throw new Error('Pre-order cart did not settle');
    }
    function refresh() {
      doc.documentElement.dispatchEvent(new w.CustomEvent('cart:refresh', {
        bubbles: true, detail: { baseEvent: 'tl:preorder-sync' }
      }));
    }
    function failed() { doc.documentElement.dataset.tlPreorderCart = 'error'; }
    function schedule() {
      w.clearTimeout(timer);
      timer = w.setTimeout(function () {
        queue(reconcile).then(function (changed) { if (changed) refresh(); }).catch(failed);
      }, 100);
    }
    // The theme's fetch-based cart writes are serialized with property repair.
    // Every line in a multi-item add is checked from server data after the add.
    w.fetch = function (input, init) {
      var url;
      try { url = new w.URL(typeof input === 'string' ? input : input.url, w.location.href); }
      catch (e) { return rawFetch(input, init); }
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (url.origin !== w.location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
        return rawFetch(input, init);
      }
      doc.documentElement.dataset.tlPreorderCart = 'checking';
      return queue(async function () {
        var response = await rawFetch(input, init);
        if (!response.ok) return response;
        try {
          var changed = await reconcile();
          if (changed) {
            // Rebuild bundled sections before callers render the response;
            // otherwise the drawer could show the pre-repair line HTML.
            var payload = await response.clone().json();
            if (payload.sections) {
              var names = Object.keys(payload.sections);
              var sections = await (await request(base + '?sections=' + encodeURIComponent(names.join(',')))).json();
              payload.sections = sections;
              response = new w.Response(JSON.stringify(payload), { status: response.status, headers: response.headers });
            } else w.setTimeout(refresh, 0);
          }
        } catch (e) {
          // The add already succeeded. Don't report a failed add and encourage
          // duplicates. Checkout always rechecks and will stop on failure.
          failed();
        }
        return response;
      });
    };
    function errorMessage(error) {
      var el = doc.getElementById('tl-preorder-check-error');
      if (!el) {
        el = doc.createElement('p'); el.id = 'tl-preorder-check-error'; el.setAttribute('role', 'alert');
        var host = doc.querySelector('cart-drawer[open]') || doc.querySelector('main') || doc.body;
        host.appendChild(el);
      }
      el.textContent = error && error.customerMessage || 'We couldn’t confirm the shipping details. Please try checkout again.';
    }
    function checkout(url, control) {
      if (checkoutBusy) return;
      checkoutBusy = true;
      checkoutControl = control || null;
      if (control) control.setAttribute('aria-busy', 'true');
      queue(reconcile).then(function () { w.location.assign(url); }).catch(function (error) {
        failed(); errorMessage(error); checkoutBusy = false;
        if (control) control.removeAttribute('aria-busy');
      });
    }
    // Capture before theme handlers, including the cart drawer's checkout form.
    doc.addEventListener('click', function (event) {
      var path = event.composedPath ? event.composedPath() : [event.target];
      var target = path.find(function (node) { return node && node.matches && node.matches('a[href],button[name="checkout"],input[name="checkout"]'); });
      if (!target) return;
      var href = target.getAttribute('href'), url;
      if (href) {
        try { url = new w.URL(href, w.location.href); } catch (e) { return; }
        if (url.origin !== w.location.origin || !/\/(checkout|checkouts)(\/|$)/.test(url.pathname)) return;
      } else url = new w.URL(base + 'checkout', w.location.origin);
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(url.href, target);
    }, true);
    doc.addEventListener('submit', function (event) {
      var form = event.target, action;
      if (!form || !form.action) return;
      try { action = new w.URL(form.action, w.location.href); } catch (e) { return; }
      var isCheckout = /\/(checkout|checkouts)(\/|$)/.test(action.pathname) ||
        (/\/cart\/?$/.test(action.pathname) && event.submitter && event.submitter.name === 'checkout');
      if (!isCheckout || action.origin !== w.location.origin) return;
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(/\/cart\/?$/.test(action.pathname) ? base + 'checkout' : action.href, event.submitter);
    }, true);
    ['cart:change', 'cart:refresh', 'cart-drawer:refreshed', 'rebuy:cart.change'].forEach(function (name) {
      doc.addEventListener(name, function (event) {
        if (event.detail && event.detail.baseEvent === 'tl:preorder-sync') return;
        schedule();
      });
    });
    w.addEventListener('pageshow', function (event) {
      // Safari can restore this same script instance from the back-forward cache.
      // The previous checkout navigation succeeded, so its busy flag must reset.
      if (event.persisted) {
        checkoutBusy = false;
        if (checkoutControl) checkoutControl.removeAttribute('aria-busy');
        checkoutControl = null;
      }
      schedule();
    });
    doc.addEventListener('visibilitychange', function () { if (!doc.hidden) schedule(); });
    // A nonempty existing cart is checked on first load even outside a PDP.
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedule, { once: true });
    else schedule();
  }
  return { propertiesFor: propertiesFor, membershipRepairFor: membershipRepairFor, install: install };
});
